/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

// src/components/AccountForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { deleteAccount } from "../api/api";
import API_BASE_URL from "../api/config";

const inputClass =
  "w-full border border-borderDefault p-2 rounded bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-2 focus:ring-primary focus:border-primary";
const readOnlyClass =
  "w-full border border-borderDefault p-2 rounded bg-backgroundBody text-textSecondary cursor-not-allowed";

const AccountForm = ({ account, refreshAccounts, closeModal }) => {
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("");
  const [credentials, setCredentials] = useState({});
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE_URL}/api/accounts/${account.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((resp) => {
        const data = resp.data;
        setName(data.name || "");
        setPlatform(data.platform || "");
        setCredentials(data.credentials || {});
        setIsDefault(!!data.is_default);
      })
      .catch((err) => console.error("Error fetching account details:", err));

    if (account.is_default !== undefined) setIsDefault(!!account.is_default);
    if (account.name) setName(account.name);
    if (account.platform) setPlatform(account.platform);
  }, [account.id]);

  const setCred = (key, value) =>
    setCredentials((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    const token = localStorage.getItem("token");
    // Strip read-only/metadata fields before sending
    const { has_password, ...cleanCreds } = credentials;
    // Don't send empty password fields — backend interprets absence as "keep existing"
    if (platform === "InteractiveBrokers" && !cleanCreds.ib_password) {
      delete cleanCreds.ib_password;
    }
    if (platform === "MT5" && !cleanCreds.password) {
      delete cleanCreds.password;
    }
    axios
      .put(
        `${API_BASE_URL}/api/accounts/${account.id}/`,
        { name, credentials: cleanCreds, is_default: isDefault },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        refreshAccounts();
        closeModal();
      })
      .catch((err) => console.error("Error updating account:", err));
  };

  const handleDeleteAccount = () => {
    if (!window.confirm("Are you sure you want to delete this account?")) return;
    const token = localStorage.getItem("token");
    deleteAccount(account.id, token)
      .then(() => {
        refreshAccounts();
        closeModal();
      })
      .catch((err) => console.error("Error deleting account:", err));
  };

  const renderCredentialFields = () => {
    if (platform === "MT5") {
      return (
        <>
          <div>
            <label className="block font-medium text-textPrimary mb-1">Account Number</label>
            <input type="text" value={credentials.account_number || ""} readOnly className={readOnlyClass} />
          </div>
          <div>
            <label className="block font-medium text-textPrimary mb-1">Broker Server</label>
            <input
              type="text"
              value={credentials.broker_server || ""}
              onChange={(e) => setCred("broker_server", e.target.value)}
              placeholder="Broker Server"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block font-medium text-textPrimary mb-1">Password</label>
            <input
              type="password"
              value={credentials.password || ""}
              onChange={(e) => setCred("password", e.target.value)}
              placeholder={credentials.has_password ? "Leave blank to keep existing" : "Enter password"}
              className={inputClass}
            />
            {credentials.has_password && !credentials.password && (
              <p className="text-xs text-green-500 mt-1">Password saved</p>
            )}
            {!credentials.has_password && !credentials.password && (
              <p className="text-xs text-yellow-500 mt-1">No password stored — enter one to connect</p>
            )}
          </div>
        </>
      );
    }

    if (platform === "InteractiveBrokers") {
      return (
        <>
          <div>
            <label className="block font-medium text-textPrimary mb-1">IB Username</label>
            <input
              type="text"
              value={credentials.ib_username || ""}
              onChange={(e) => setCred("ib_username", e.target.value)}
              placeholder="IB Username"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block font-medium text-textPrimary mb-1">IB Password</label>
            <input
              type="password"
              value={credentials.ib_password || ""}
              onChange={(e) => setCred("ib_password", e.target.value)}
              placeholder={credentials.has_password ? "Leave blank to keep existing" : "Enter IB password"}
              className={inputClass}
            />
            {credentials.has_password && !credentials.ib_password && (
              <p className="text-xs text-green-500 mt-1">Password saved</p>
            )}
            {!credentials.has_password && !credentials.ib_password && (
              <p className="text-xs text-yellow-500 mt-1">No password stored — enter one to connect</p>
            )}
          </div>
          <div>
            <label className="block font-medium text-textPrimary mb-1">Account ID</label>
            <input type="text" value={credentials.ib_account_id || ""} readOnly className={readOnlyClass} />
          </div>
          <div>
            <label className="block font-medium text-textPrimary mb-1">Environment</label>
            <input type="text" value={credentials.environment || ""} readOnly className={readOnlyClass} />
          </div>
        </>
      );
    }

    if (platform === "cTrader") {
      return (
        <>
          <div>
            <label className="block font-medium text-textPrimary mb-1">Account Number</label>
            <input type="text" value={credentials.account_number || ""} readOnly className={readOnlyClass} />
          </div>
          <div>
            <label className="block font-medium text-textPrimary mb-1">Broker</label>
            <input type="text" value={credentials.broker || ""} readOnly className={readOnlyClass} />
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="bg-backgroundSurface p-4 rounded shadow">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-lg font-semibold text-textPrimary">Edit Account</h4>
        {platform && (
          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
            {platform}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block font-medium text-textPrimary mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Account Name"
            className={inputClass}
          />
        </div>
        {renderCredentialFields()}
      </div>
      {/* Default Account Toggle */}
      <div className="flex items-center gap-3 mt-4">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-borderDefault peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-borderDefault after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
        </label>
        <span className="text-sm font-medium text-textPrimary">Set as Default Account</span>
      </div>
      <div className="flex justify-between mt-6 gap-4">
        <button
          onClick={handleDeleteAccount}
          className="btn btn-primary w-full disabled:opacity-50"
        >
          Delete
        </button>
        <button
          onClick={handleSave}
          className="btn btn-primary w-full disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default AccountForm;
